import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';

import { useAuth } from 'modules/auth/auth.context';
import { ExpandableTable, SectionTitle, SegmentedControl } from 'modules/shared/components';
import { capitalizeString, formatAmount } from 'modules/shared/utils';
import { VoucherRequestModal } from '../components';
import {
  PRIMARY_VIEW_OPTIONS,
  VOUCHER_PRIMARY_VIEWS,
  VOUCHER_STATUS,
  VOUCHER_STATUS_KEYS,
} from '../constants';
import { useMyVouchers } from '../hooks/useMyVouchers';
import { getEmptyMessage, getVisibleVouchers } from '../utils';

/**
 * @param {Object} props
 * @param {'active' | 'closed'} props.view
 * @returns {Array<{id: string, label: string, dataKey: string, renderCell: (value: any) => JSX.Element}>}
 */
function getMyVouchersColumns() {
  const columns = [
    { id: 'date', label: 'Date', dataKey: 'dateOfEvent', sortable: true },
    { id: 'name', label: 'Name', dataKey: 'employeeName', sortable: true },
    {
      id: 'department',
      label: 'Department',
      dataKey: 'department',
      sortable: true,
      renderCell: capitalizeString,
    },
    { id: 'type', label: 'Request type', dataKey: 'expenseType', sortable: true },
    {
      id: 'amount',
      label: 'Amount',
      dataKey: 'amountRequested',
      sortable: true,
      renderCell: formatAmount,
    },
    {
      id: 'status',
      label: 'Status',
      dataKey: 'status',
      renderCell: (val) => {
        const config = VOUCHER_STATUS[val];

        if (!config) {
          return <span>—</span>;
        }
        return (
          <Typography sx={{ color: `${config.color}.main`, fontWeight: 500 }}>
            {config.label ?? '-'}
          </Typography>
        );
      },
    },
  ];

  return columns;
}

export default function MyVouchersSection() {
  const { user } = useAuth();
  const employeeId = user?.employee_id;

  const { vouchersByStatus, loading, refresh } = useMyVouchers(employeeId);

  const [view, setView] = useState(VOUCHER_PRIMARY_VIEWS.ACTIVE);

  const visibleVouchers = useMemo(() => {
    return getVisibleVouchers(vouchersByStatus, view);
  }, [view, vouchersByStatus]);

  const emptyMessage = getEmptyMessage(view);

  const columns = useMemo(() => getMyVouchersColumns(), []);

  return (
    <div className='column gap-4'>
      <SectionTitle
        title='Vouchers & Office Supplies Request'
        action={<VoucherRequestModal onRefresh={refresh} />}
      />
      <div className='column gap-3'>
        <SegmentedControl
          options={PRIMARY_VIEW_OPTIONS}
          value={view}
          onChange={(v: string) => setView(v)}
          ariaLabel='Voucher view'
        />
      </div>

      <ExpandableTable
        columns={columns}
        data={visibleVouchers}
        loading={loading}
        emptyMessage={emptyMessage}
        headerBg
        renderExpandedRow={(voucher) => <MyVouchersExpandedRow voucherData={voucher} />}
      />
    </div>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.voucherData
 * @returns {JSX.Element}
 */
function MyVouchersExpandedRow({ voucherData }) {
  return (
    <div className='column'>
      <div className='row row--between'>
        <div>
          <span>Applied on: </span>
          <span className='font-medium'>{voucherData.appliedOn}</span>
        </div>
        <div className='row gap-3'>
          <span className='font-medium'>Note</span>
          <p>{voucherData.employeeNote || '—'}</p>
        </div>
      </div>
      {voucherData.status === VOUCHER_STATUS_KEYS.REJECTED && (
        <div className='row gap-3'>
          <span className='font-medium'>Reason for Rejection</span>
          <p>{voucherData.rejectionReason || '-'}</p>
        </div>
      )}

      {voucherData.attachmentUrl && (
        <div className='row gap-3'>
          <span className='font-medium'>Bill / attachment</span>
          <MuiLink href={voucherData.attachmentUrl} target='_blank' rel='noopener noreferrer'>
            Open attachment
          </MuiLink>
        </div>
      )}
    </div>
  );
}
